<template>
	<div id="app">
		<transition :name="transitionName">
			<router-view />
		</transition>

		<MenuButton :class="$style.menuButton"></MenuButton>

		<Instructions></Instructions>

		<PrizeModal></PrizeModal>

		<FullScreenOverlay></FullScreenOverlay>

		<SeeThroughButton
			:class="[$style.seeThroughButton, {[$style.top]: isWinButtonTop}]"
			:text="winButtonText"
			@click.native="handleWinButtonClick"
			v-touch="handleWinButtonClick"
			v-show="(isMobile && appState !== 'playing-show-card' && appState !== 'instruction') || !isMobile"
		></SeeThroughButton>
		<transition name="fade">
			<loader v-show="showLoader"></loader>
		</transition>
	</div>
</template>

<script>
Object.unfreeze = function(o) {
	var oo = undefined;
	if (o instanceof Array) {
		oo = [];
		var clone = function(v) {
			oo.push(v);
		};
		o.forEach(clone);
	} else if (o instanceof String) {
		oo = new String(o).toString();
	} else if (typeof o == "object") {
		oo = {};
		for (var property in o) {
			oo[property] = o[property];
		}
	}
	return oo;
};
window.THREE = Object.unfreeze(require("three"));

import WebFont from "webfontloader";
import EventPolyfill from "custom-event-polyfill";
import PromisedLoad from "./plugins/PromisedLoad.js";
import Instructions from "./components/Instructions";
import PrizeModal from "./components/PrizeModal";
import FullScreenOverlay from "./components/FullScreenOverlay";
import MenuButton from "./components/MenuButton";
import SeeThroughButton from "./components/SeeThroughButton";
import loader from "./components/Loader.vue";
import isMobileMixin from "./plugins/isMobileMixin.js";
import tweenSpeedsMixin from "./plugins/tweenSpeedsMixin.js";

export default {
	name: "App",
	components: {
		Instructions,
		PrizeModal,
		FullScreenOverlay,
		MenuButton,
		loader,
		SeeThroughButton
	},
	mixins: [isMobileMixin, tweenSpeedsMixin],
	beforeCreate() {
		this.$store.dispatch("content/retrieve").then(response => {
			console.log("retrieved info: ", response);
		});
	},
	beforeMount() {
		WebFont.load({
			google: {
				families: ["Montserrat:300,400,600,700,800"]
			}
		});

		this.loadScenes();
	},
	mounted() {
		if (!this.isMobile) {
			this.setWinButtonMoveAmount();
		}
		this.isWinButtonTop = this.$route.path !== "/";
		window.addEventListener("resize", this.setWinButtonMoveAmount);

		this.$bus.$on("loader/toggle", this.toggleLoader);

		this.$bus.$on("fade-out-win-button", this.fadeOutWinButton);
	},
	data() {
		return {
			textureSwapper: {
				fadeColor: new THREE.Color(0x2f6619),
				unfadeColor: new THREE.Color(0xffffff)
			},
			transitionName: "fade",
			isWinButtonLeft: false,
			isWinButtonTop: false,
			winButtonMoveAmount: null,
			showLoader: true
		};
	},
	computed: {
		meshes: function() {
			return this.$store.getters.getMeshes;
		},
		textures: function() {
			return this.$store.getters.getTextures;
		},
		residential: function() {
			return this.$store.getters.getResidential;
		},
		commercial: function() {
			return this.$store.getters.getCommercial;
		},
		appState: function() {
			return this.$store.getters.getAppState;
		},
		winButtonText: function() {
			if (this.isMobile) {
				return "Enter to win an emergency preparedness kit from Preppi";
			} else {
				return "Enter to win an emergency preparedness kit from Preppi";
			}
		}
	},
	watch: {
		$route(to, from) {
			// set the transition name dynamically so we only do the fade when going from the mainview
			if (from.path === "/") {
				this.transitionName = "fade";
			} else {
				this.transitionName = "";
			}

			this.isWinButtonTop = to.path !== "/";
		},
		appState(state, oldState) {
			// hide menu button if doing intro
			if (state === "intro-anim") {
				this.hideMenuButton();
				this.showWinButton();
			} else {
				this.showMenuButton();
			}

			if (this.isMobile) {
				return;
			}

			if (state === "instruction" || state === "playing-show-card") {
				// move the win button left if its not already
				if (!this.isWinButtonLeft) {
					this.moveWinButtonLeft();
				}
			} else {
				// move the win button back to start pos if not already there
				if (this.isWinButtonLeft) {
					this.moveWinButtonRight();
				}
			}
		}
	},
	methods: {
		hideMenuButton() {
			TweenMax.to("." + this.$style.menuButton, 0.5, {
				opacity: 0
			});
		},
		showMenuButton() {
			TweenMax.to("." + this.$style.menuButton, 0.5, {
				opacity: 1
			});
		},
		setWinButtonMoveAmount() {
			this.winButtonMoveAmount = window.innerWidth * 0.3;
		},
		moveWinButtonRight() {
			TweenMax.to("." + this.$style.seeThroughButton, 0.5, {
				x: 0,
				onComplete: () => {
					this.isWinButtonLeft = false;
				}
			});
		},
		moveWinButtonLeft() {
			TweenMax.to("." + this.$style.seeThroughButton, 0.5, {
				x: "-=" + this.winButtonMoveAmount,
				onComplete: () => {
					this.isWinButtonLeft = true;
				}
			});
		},
		handleWinButtonClick() {
			this.$bus.$emit("show-prize-modal");
		},
		loadScenes: async function() {
			let residentialScene = await PromisedLoad.GetObject(
				this.residential.url
			);
			let commercialScene = await PromisedLoad.GetGLTF(
				this.commercial.url
			);
			console.log("residentialScene:  ", residentialScene);
			console.log("commercialScene:  ", commercialScene.scene);
			await this.setupResidential(residentialScene);
			await this.setupCommercial(commercialScene.scene);
			this.$bus.residentialScene = residentialScene;
			this.$bus.commercialScene = commercialScene.scene;
			this.$bus.scenesLoaded = true;
			this.$bus.$emit("scenes-loaded");
		},

		setupResidential: async function(scene) {
			scene.traverse(function(child) {
				if (child instanceof THREE.Mesh) {
					child.material.transparent = true;
					child.material.flatShading = THREE.SmoothShading;
					child.material.color = new THREE.Color(1, 1, 1);
				}
			});
			for (let room of this.residential.rooms) {
				this.residential.meshes.set(
					room.id,
					scene.getObjectByName(`${room.id}`, true)
				);
			}
			this.residential.meshes.set(
				"Fire",
				scene.getObjectByName("Fire", true)
			);

			if (this.$browserInfo.os === "iOS") {
				return Promise.all(this.setupResidentialTexturesForiOS(scene));
			} else {
				return Promise.all(this.setupResidentialTextures(scene));
			}
		},

		setupCommercial: async function(scene) {
			scene.traverse(function(child) {
				if (child instanceof THREE.Mesh) {
					child.material.transparent = true;
					child.material.flatShading = THREE.SmoothShading;
					child.material.color = new THREE.Color(1, 1, 1);
				}
			});

			for (let room of this.commercial.rooms) {
				this.commercial.meshes.set(
					room.id,
					scene.getObjectByName(room.id, true)
				);
			}

			// add exterior vegetation
			this.commercial.meshes.set(
				"ExteriorVegetation",
				scene.getObjectByName("ExteriorVegetation", true)
			);

			// add environment sphere
			this.commercial.meshes.set(
				"EnvironmentSphere",
				scene.getObjectByName("EnvironmentSphere", true)
			);

			// add shadow disc
			this.commercial.meshes.set(
				"ShadowDisc",
				scene.getObjectByName("ShadowDisc", true)
			);

			// add parking area
			this.commercial.meshes.set(
				"ParkingArea",
				scene.getObjectByName("ParkingArea", true)
			);

			return Promise.all(this.setupCommercialTextures(scene));
		},

		setupCommercialTextures: function(scene) {
			let promises = [];

			// set skybox texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Skybox.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set(
							"EnvironmentSphere",
							texture
						);
						resolve();
					}
				)
			);

			// set fileroom texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_FileRoom.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("FileRoom", texture);
						console.log("this.commercial:  ", this.commercial);
						console.log("texture:  ", texture);
						resolve();
					}
				)
			);

			// set fileroom bw texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_FileRoom_BW.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("FileRoomBW", texture);
						resolve();
					}
				)
			);

			// set warehouse texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Warehouse.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("Warehouse", texture);
						resolve();
					}
				)
			);

			// set warehouse bw texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Warehouse_BW.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("WarehouseBW", texture);
						resolve();
					}
				)
			);

			// set exterior vegetation (and parking area!) texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Exterior.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						let textureClone = texture.clone();

						// manually set up the veg and parking area since they share the same texture (we have to have separate materials and textures)

						// get mesh
						let extVegMesh = scene.getObjectByName(
							"ExteriorVegetation"
						);
						let parkingAreaMesh = scene.getObjectByName(
							"ParkingArea"
						);

						// create materials
						extVegMesh.material = new THREE.MeshBasicMaterial({
							transparent: true,
							map: texture
						});
						parkingAreaMesh.material = new THREE.MeshBasicMaterial({
							transparent: true,
							map: textureClone
						});

						extVegMesh.material.needsUpdate = true;
						parkingAreaMesh.material.needsUpdate = true;
						texture.needsUpdate = true;
						textureClone.needsUpdate = true;

						this.commercial.textures.set(
							"ExteriorVegetation",
							texture
						);
						this.commercial.textures.set(
							"ParkingArea",
							textureClone
						);
						resolve();
					}
				)
			);

			// set exterior vegetation (and parking area!) bw texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Exterior_BW.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						let textureClone = texture.clone();

						texture.needsUpdate = true;
						textureClone.needsUpdate = true;

						this.commercial.textures.set(
							"ExteriorVegetationBW",
							texture
						);
						this.commercial.textures.set(
							"ParkingAreaBW",
							textureClone
						);
						resolve();
					}
				)
			);

			// set lobby texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Lobby.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("Lobby", texture);
						resolve();
					}
				)
			);

			// set lobby bw texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Lobby_BW.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("LobbyBW", texture);
						resolve();
					}
				)
			);

			// set outershell texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_OuterShell.jpg",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("OuterShell", texture);
						resolve();
					}
				)
			);

			// set shadow disc texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/commercial/textures/Servpro_Shadow.png",
					(texture, resolve) => {
						this.processTexture(texture);
						this.commercial.textures.set("ShadowDisc", texture);
						resolve();
					}
				)
			);

			return promises;
		},
		processTexture(texture) {
			// need to do things to the texture to match how the texture is set up in the gltf
			texture.flipY = false;
			texture.encoding = THREE.sRGBEncoding;
		},
		setupResidentialTexturesForiOS(scene) {
			let promises = [];
			const exteriors = [
				scene.getObjectByName("SecondFloorWalls", true),
				scene.getObjectByName("SecondFloorRoof", true),
				scene.getObjectByName("FirstFloorWalls", true),
				scene.getObjectByName("FirstFloorRoof", true)
			];

			// load skybox
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/SkyboxFullerest.jpg",
					(skyboxTexture, resolve) => {
						var sphereGeo = new THREE.SphereGeometry(30, 30, 30);
						var sphereMat = new THREE.MeshBasicMaterial({
							transparent: true,
							map: skyboxTexture,
							side: THREE.BackSide
						});
						let skybox = new THREE.Mesh(sphereGeo, sphereMat);
						this.residential.meshes.set("Skybox", skybox);
						skybox.position.set(0, 5, 0);
						scene.add(skybox);
						resolve();
					}
				)
			);

			// set colored room textures
			for (let room of this.residential.rooms) {
				// load colored room textures
				promises.push(
					PromisedLoad.GetTexture(
						`/static/house/textures/Servpro_${room.id}.jpg`,
						(texture, resolve) => {
							this.residential.textures.set(room.id, texture);
							let mesh = this.residential.meshes.get(room.id);
							mesh.material = new THREE.MeshBasicMaterial({
								transparent: true,
								map: texture
							});

							resolve();
						}
					)
				);
			}

			// load Kitchen color texture
			// promises.push(
			// 	PromisedLoad.GetTexture(
			// 		`/static/house/textures/Servpro_Kitchen.jpg`,
			// 		(texture, resolve) => {
			// 			this.residential.textures.set("Kitchen", texture);
			// 			let mesh = this.residential.meshes.get("Kitchen");
			// 			mesh.material = new THREE.MeshBasicMaterial({
			// 				transparent: true,
			// 				map: texture
			// 			});
			// 			resolve();
			// 		}
			// 	)
			// );

			// load Livingroom color texture
			// promises.push(
			// 	PromisedLoad.GetTexture(
			// 		`/static/house/textures/Servpro_Livingroom.jpg`,
			// 		(texture, resolve) => {
			// 			this.residential.textures.set("Livingroom", texture);
			// 			let mesh = this.residential.meshes.get("Livingroom");
			// 			mesh.material = new THREE.MeshBasicMaterial({
			// 				transparent: true,
			// 				map: texture
			// 			});
			// 			resolve();
			// 		}
			// 	)
			// );

			// load Bathroom color texture
			// promises.push(
			// 	PromisedLoad.GetTexture(
			// 		`/static/house/textures/Servpro_Bathroom.jpg`,
			// 		(texture, resolve) => {
			// 			this.residential.textures.set("Bathroom", texture);
			// 			let mesh = this.residential.meshes.get("Bathroom");
			// 			mesh.material = new THREE.MeshBasicMaterial({
			// 				transparent: true,
			// 				map: texture
			// 			});
			// 			resolve();
			// 		}
			// 	)
			// );

			// // load Bedroom color texture
			// promises.push(
			// 	PromisedLoad.GetTexture(
			// 		`/static/house/textures/Servpro_Bedroom.jpg`,
			// 		(texture, resolve) => {
			// 			this.residential.textures.set("Bedroom", texture);
			// 			let mesh = this.residential.meshes.get("Bedroom");
			// 			mesh.material = new THREE.MeshBasicMaterial({
			// 				transparent: true,
			// 				map: texture
			// 			});
			// 			resolve();
			// 		}
			// 	)
			// );

			// load texture for fire
			promises.push(
				PromisedLoad.GetTexture(
					`/static/house/textures/Servpro_Livingroom_BW.jpg`,
					(textureBW, resolve) => {
						let fire = this.residential.meshes.get("Fire");
						let fireMat = new THREE.MeshBasicMaterial({
							transparent: true,
							map: textureBW,
							color: this.textureSwapper.fadeColor
						});
						fire.material = fireMat;
						resolve();
					}
				)
			);

			// load exterior texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Exterior.jpg",
					(exteriorTexture, resolve) => {
						exteriors.forEach(exterior => {
							let exteriorMat = new THREE.MeshBasicMaterial({
								transparent: true,
								map: exteriorTexture
							});
							exterior.material = exteriorMat;
							exterior.material.needsUpdate = true;
						});
						resolve();
					}
				)
			);

			// load shadow texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Shadow2.jpg",
					(shadowTexture, resolve) => {
						this.textures.set("ShadowExterior", shadowTexture);
						PromisedLoad.GetObject(
							"/static/house/shadowdisk.json"
						).then(shadowScene => {
							let shadow = shadowScene.children[0].children[0];
							this.residential.meshes.set("Shadow", shadow);
							let shadowMat = new THREE.MeshBasicMaterial({
								transparent: true,
								map: shadowTexture
							});
							shadow.material = shadowMat;
							shadow.material.needsUpdate = true;
							scene.add(shadowScene);
							resolve();
						});
					}
				)
			);

			// load interior shadow texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_ShadowInterior.jpg",
					(interiorShadowTexture, resolve) => {
						this.residential.textures.set(
							"ShadowInterior",
							interiorShadowTexture
						);
						resolve();
					}
				)
			);

			// load enviro texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Envrionmental.jpg",
					(veggieTexture, resolve) => {
						PromisedLoad.GetObject(
							"/static/house/vegetation.json",
							veggieScene => {
								scene.add(veggieScene);
								this.residential.meshes.set(
									"Veggies",
									veggieScene
								);
								let veg = veggieScene.children[0].children[0];
								let vegMaterial = new THREE.MeshBasicMaterial({
									map: veggieTexture,
									transparent: true,
									flatShading: THREE.SmoothShading
								});
								veg.material = vegMaterial;
								veg.material.needsUpdate = true;
								resolve();
							}
						);
					}
				)
			);

			return promises;
		},
		setupResidentialTextures: function(scene) {
			let promises = [];
			const exteriors = [
				scene.getObjectByName("SecondFloorWalls", true),
				scene.getObjectByName("SecondFloorRoof", true),
				scene.getObjectByName("FirstFloorWalls", true),
				scene.getObjectByName("FirstFloorRoof", true)
			];

			// load skybox
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/SkyboxFullerest.jpg",
					(skyboxTexture, resolve) => {
						var sphereGeo = new THREE.SphereGeometry(30, 30, 30);
						var sphereMat = new THREE.MeshBasicMaterial({
							transparent: true,
							map: skyboxTexture,
							side: THREE.BackSide
						});
						let skybox = new THREE.Mesh(sphereGeo, sphereMat);
						this.residential.meshes.set("Skybox", skybox);
						skybox.position.set(0, 5, 0);
						scene.add(skybox);
						resolve();
					}
				)
			);

			for (let room of this.residential.rooms) {
				// load colored room textures
				promises.push(
					PromisedLoad.GetTexture(
						`/static/house/textures/Servpro_${room.id}.jpg`,
						(texture, resolve) => {
							this.residential.textures.set(room.id, texture);
							let mesh = this.residential.meshes.get(room.id);
							mesh.material = new THREE.MeshBasicMaterial({
								transparent: true,
								map: texture
							});
							resolve();
						}
					)
				);

				// load bw room textures
				promises.push(
					PromisedLoad.GetTexture(
						`/static/house/textures/Servpro_${room.id}_BW.jpg`,
						(textureBW, resolve) => {
							if (room.id === "Livingroom") {
								let fire = this.residential.meshes.get("Fire");
								let fireMat = new THREE.MeshBasicMaterial({
									transparent: true,
									map: textureBW,
									color: this.textureSwapper.fadeColor
								});
								fire.material = fireMat;
							}

							if (room.id != "Kitchen") {
								let mesh = this.residential.meshes.get(room.id);
								let meshMaterial = new THREE.MeshBasicMaterial({
									transparent: true,
									map: textureBW,
									color: this.textureSwapper.fadeColor
								});
								mesh.material = meshMaterial;
								console.log(
									"texture image:  ",
									mesh.material.map.image.currentSrc
								);
							}

							this.residential.textures.set(
								`${room.id}BW`,
								textureBW
							);

							resolve();
						}
					)
				);
			}

			// load exterior texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Exterior.jpg",
					(exteriorTexture, resolve) => {
						exteriors.forEach(exterior => {
							let exteriorMat = new THREE.MeshBasicMaterial({
								transparent: true,
								map: exteriorTexture
							});
							exterior.material = exteriorMat;
							exterior.material.needsUpdate = true;
						});
						resolve();
					}
				)
			);

			// load shadow texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Shadow2.jpg",
					(shadowTexture, resolve) => {
						this.textures.set("ShadowExterior", shadowTexture);
						PromisedLoad.GetObject(
							"/static/house/shadowdisk.json"
						).then(shadowScene => {
							let shadow = shadowScene.children[0].children[0];
							this.residential.meshes.set("Shadow", shadow);
							let shadowMat = new THREE.MeshBasicMaterial({
								transparent: true,
								map: shadowTexture
							});
							shadow.material = shadowMat;
							shadow.material.needsUpdate = true;
							scene.add(shadowScene);
							resolve();
						});
					}
				)
			);

			// load interior shadow texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_ShadowInterior.jpg",
					(interiorShadowTexture, resolve) => {
						this.residential.textures.set(
							"ShadowInterior",
							interiorShadowTexture
						);
						resolve();
					}
				)
			);

			// load enviro texture
			promises.push(
				PromisedLoad.GetTexture(
					"/static/house/textures/Servpro_Envrionmental.jpg",
					(veggieTexture, resolve) => {
						PromisedLoad.GetObject(
							"/static/house/vegetation.json",
							veggieScene => {
								scene.add(veggieScene);
								this.residential.meshes.set(
									"Veggies",
									veggieScene
								);
								let veg = veggieScene.children[0].children[0];
								let vegMaterial = new THREE.MeshBasicMaterial({
									map: veggieTexture,
									transparent: true,
									flatShading: THREE.SmoothShading
								});
								veg.material = vegMaterial;
								veg.material.needsUpdate = true;
								resolve();
							}
						);
					}
				)
			);
			return promises;
		},
		fadeOutWinButton() {
			TweenMax.to("." + this.$style.seeThroughButton, 0.5, {
				autoAlpha: 0
			});
		},
		showWinButton() {
			TweenMax.to("." + this.$style.seeThroughButton, 0.5, {
				autoAlpha: 1
			});
		},
		toggleLoader(newValue) {
			console.log("toggleLoader(newValue)", newValue);
			this.showLoader = newValue;
		}
	}
};
</script>

<style lang='scss' module>
@import "./assets/css/globals.scss";

.see-through-button {
	position: absolute;
	left: 50%;
	bottom: 25px;
	z-index: 725;
	max-width: none;
	width: calc(100% - 100px);

	transform: translateX(-50%);

	&.top {
		width: 230px;
		font-size: 14px;
		max-width: 300px;
		top: 25px;
		bottom: auto;
		left: auto;
		right: 25px;
		transform: translateX(0);
		padding-top: 10px;
		padding-bottom: 10px;
		padding-left: 12px;
		padding-right: 12px;
	}

	@include medium {
		max-width: 300px;
	}

	@include large {
		// the seethriygh button styling is used in a lot of places that are all positioned VERY differently.
		// margin-right: 45px;
	}
}

.menu-button {
	content: "";
}
</style>

<style>
@import "normalize.css";
#app {
	font-family: "Montserrat", sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	width: 100%;
	height: 100%;
}

html,
body {
	margin: 0px;
	overflow: hidden;
	width: 100%;
	height: 100%;
	position: fixed;
	top: 0px;
	left: 0px;
}
strong {
	font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/App.vue